import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  BookOpen, Calendar, ChevronRight, ChevronDown, ChevronLeft,
  Search, X, Download, Upload, Loader2, AlertTriangle, SlidersHorizontal,
} from 'lucide-react';
import EdefterUpload from './EdefterUpload';
import Select from 'react-select';
import { useCompany } from '../../contexts/CompanyContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import { EDEFTER_API_URL } from '../../config/edefterApi';
import AuthService from '../../services/authService';
import { SectionHeader } from '../../components/common/SectionHeader';

const cardClass =
  'bg-white dark:bg-[#1C1C1E] rounded-[28px] border border-gray-100 dark:border-white/5 shadow-sm';

const inputClass =
  'w-full h-12 px-4 rounded-xl border text-sm transition-all outline-none ' +
  'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 ' +
  'text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 ' +
  'focus:border-[#24299d] focus:ring-2 focus:ring-[#24299d]/20 dark:focus:border-[#5a61e0]';

const getSelectStyles = (darkMode) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 12,
    borderColor: state.isFocused ? '#24299d' : darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(36,41,157,0.2)' : 'none',
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
    '&:hover': { borderColor: '#24299d' },
    cursor: state.isDisabled ? 'not-allowed' : 'default',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    backgroundColor: darkMode ? '#1C1C1E' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: state.isSelected
      ? '#24299d'
      : state.isFocused
        ? darkMode ? 'rgba(255,255,255,0.08)' : '#f3f4f6'
        : 'transparent',
    color: state.isSelected ? 'white' : darkMode ? '#e5e7eb' : '#111827',
    cursor: 'pointer',
    margin: '2px 4px',
    width: 'calc(100% - 8px)',
  }),
  singleValue: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  placeholder: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  input: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  clearIndicator: (base) => ({
    ...base, color: darkMode ? '#6b7280' : '#9ca3af',
    '&:hover': { color: '#ef4444' },
  }),
});

const GeneralJournal = () => {
  const { taxId } = useCompany();
  const { t } = useLanguage();
  const { darkMode } = useAppTheme();
  const selectedIdentifier = taxId;
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [entryHeaders, setEntryHeaders] = useState([]); // Sadece header'lar
  const [entryDetailsCache, setEntryDetailsCache] = useState(new Map()); // EntryHeader ID -> details
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // Hangi header'ların detayları yükleniyor
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(50); // Sayfa başına kayıt sayısı
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [accountCodeSearch, setAccountCodeSearch] = useState('');
  const [debouncedAccountCodeSearch, setDebouncedAccountCodeSearch] = useState('');
  const [shouldExpandAll, setShouldExpandAll] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchDocuments = useCallback(async (selectDocumentId = null) => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      return;
    }
    try {
      setLoadingDocuments(true);
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/documents-by-identifier?identifier=${selectedIdentifier}`
      );
      if (response.ok) {
        const data = await response.json();
        const sortedDocuments = (data || []).sort((a, b) => {
          const dateA = new Date(a.periodCoveredStart);
          const dateB = new Date(b.periodCoveredStart);
          const yearA = dateA.getFullYear();
          const monthA = dateA.getMonth() + 1;
          const yearB = dateB.getFullYear();
          const monthB = dateB.getMonth() + 1;
          if (yearB !== yearA) return yearB - yearA;
          return monthA - monthB;
        });
        setDocuments(sortedDocuments);
        if (selectDocumentId) {
          const doc = sortedDocuments.find((d) => d.id === selectDocumentId);
          if (doc) {
            setSelectedDocument(doc);
            setPage(1);
            setExpandedEntries(new Set());
            setEntryDetailsCache(new Map());
            setShouldExpandAll(false);
          }
        }
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }, [selectedIdentifier]);

  const handleUploadSuccess = useCallback(({ documentId }) => {
    if (documentId) {
      fetchDocuments(documentId);
    } else {
      fetchDocuments();
    }
  }, [fetchDocuments]);

  // TaxId değiştiğinde state'leri temizle
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setError(null);
      return;
    }
  }, [selectedIdentifier]);

  // Fetch documents when identifier is selected
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Track previous search value to detect actual changes
  const prevAccountCodeSearchRef = React.useRef('');

  // Debounce account code search (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update debounced value if it actually changed
      if (prevAccountCodeSearchRef.current !== accountCodeSearch) {
        // Reset page and clear cache when search term changes
        if (prevAccountCodeSearchRef.current !== '' || accountCodeSearch !== '') {
          setPage(1);
          setEntryDetailsCache(new Map());
        }
        
        prevAccountCodeSearchRef.current = accountCodeSearch;
        setDebouncedAccountCodeSearch(accountCodeSearch);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [accountCodeSearch]);


  // Fetch entry headers when document is selected, page changes, or search changes
  useEffect(() => {
    if (!selectedDocument) {
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    const fetchEntryHeaders = async () => {
      try {
        setLoading(true);
        setError(null);
        const pageIndex = page - 1; // Backend 0-based, frontend 1-based
        
        // Build URL with optional accountCode parameter
        let url = `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=${pageIndex}&size=${rowsPerPage}`;
        if (debouncedAccountCodeSearch && debouncedAccountCodeSearch.trim()) {
          url += `&accountCode=${encodeURIComponent(debouncedAccountCodeSearch.trim())}`;
        }
        
        const response = await AuthService.authenticatedFetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.error) {
            console.error('[GeneralJournal] Backend error:', data.error);
            setError(data.error);
            return;
          }
          
          const newHeaders = data.entryHeaders || [];
          setEntryHeaders(newHeaders);
          setTotalPages(data.totalPages || 0);
          setTotalElements(data.totalElements || 0);

          // If shouldExpandAll is true (user manually enabled), expand all entries and load their details
          if (shouldExpandAll && newHeaders.length > 0) {
            const allHeaderIds = new Set(newHeaders.map(h => h.id));
            allHeaderIds.forEach(headerId => {
              if (!entryDetailsCache.has(headerId)) {
                fetchEntryDetails(headerId);
              }
            });
            setExpandedEntries(allHeaderIds);
          } else if (!shouldExpandAll) {
            // Reset expanded entries only if expand all is not active (user manually disabled)
            setExpandedEntries(new Set());
          }
          // Note: Background fetching for search is handled in separate useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
        } else {
          const errorText = await response.text();
          console.error('[GeneralJournal] Error response:', errorText);
          setError('Failed to load journal entries: ' + errorText);
        }
      } catch (err) {
        console.error('Error fetching entry headers:', err);
        setError('Failed to load journal entries: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEntryHeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, page, rowsPerPage, debouncedAccountCodeSearch]);

  // Background fetch: Load details for all headers when search is active (silently, without expanding)
  // Use batch request instead of individual requests for better performance
  useEffect(() => {
    const hasSearch = debouncedAccountCodeSearch && debouncedAccountCodeSearch.trim();
    if (hasSearch && entryHeaders.length > 0 && selectedDocument) {
      // Collect all header IDs that need details
      const headerIdsToFetch = entryHeaders
        .map(header => header.id)
        .filter(id => !entryDetailsCache.has(id) && !loadingDetails.has(id));

      if (headerIdsToFetch.length > 0) {
        // Use batch request instead of individual requests
        fetchEntryDetailsBatch(headerIdsToFetch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryHeaders, debouncedAccountCodeSearch, selectedDocument]);

  // Helper function to flatten entry details
  const flattenEntryDetails = useCallback((entryHeaderData) => {
    const flattenedDetails = [];
    if (entryHeaderData?.entryDetails && Array.isArray(entryHeaderData.entryDetails)) {
      entryHeaderData.entryDetails.forEach((detail) => {
        if (detail.accounts && Array.isArray(detail.accounts) && detail.accounts.length > 0) {
          detail.accounts.forEach((account) => {
            flattenedDetails.push({
              entryDetailId: detail.id,
              lineNumber: detail.lineNumber,
              postingDate: detail.postingDate,
              amount: detail.amount,
              debitCreditCode: detail.debitCreditCode,
              documentType: detail.documentType,
              documentNumber: detail.documentNumber,
              documentDate: detail.documentDate,
              detailComment: detail.detailComment,
              accountMainId: account.mainId,
              accountMainDescription: account.mainDescription,
              accountSubId: account.accountSub?.subId || null,
              accountSubDescription: account.accountSub?.subDescription || null,
            });
          });
        } else {
          flattenedDetails.push({
            entryDetailId: detail.id,
            lineNumber: detail.lineNumber,
            postingDate: detail.postingDate,
            amount: detail.amount,
            debitCreditCode: detail.debitCreditCode,
            documentType: detail.documentType,
            documentNumber: detail.documentNumber,
            documentDate: detail.documentDate,
            detailComment: detail.detailComment,
            accountMainId: null,
            accountMainDescription: null,
            accountSubId: null,
            accountSubDescription: null,
          });
        }
      });
    }
    return flattenedDetails;
  }, []);

  // Fetch entry details in batch (for multiple headers at once)
  const fetchEntryDetailsBatch = useCallback(async (entryHeaderIds) => {
    if (!entryHeaderIds || entryHeaderIds.length === 0) return;

    // Filter out already cached or currently loading IDs
    const idsToFetch = entryHeaderIds.filter(id => 
      !entryDetailsCache.has(id) && !loadingDetails.has(id)
    );

    if (idsToFetch.length === 0) return;

    try {
      // Mark all as loading
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        idsToFetch.forEach(id => newSet.add(id));
        return newSet;
      });


      // Create comma-separated string of IDs
      const idsString = idsToFetch.join(',');
      
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entry-details-batch?entryHeaderIds=${idsString}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          console.error('[GeneralJournal] Backend error:', data.error);
          // Fallback to individual requests if batch fails
          idsToFetch.forEach(id => fetchEntryDetails(id));
          return;
        }

        // Process batch response - expect array of entry headers with details
        const batchResults = Array.isArray(data) ? data : (data.results || data.entryHeaders || []);
        
        // Update cache for all fetched headers
        const newCache = new Map(entryDetailsCache);
        batchResults.forEach((entryHeaderData) => {
          if (entryHeaderData?.entryHeaderId) {
            const headerId = entryHeaderData.entryHeaderId;
            const flattenedDetails = flattenEntryDetails(entryHeaderData);
            newCache.set(headerId, flattenedDetails);
          }
        });
        
        setEntryDetailsCache(newCache);
      } else {
        // If batch endpoint doesn't exist, fallback to individual requests
        console.warn('[GeneralJournal] Batch endpoint not available, falling back to individual requests');
        idsToFetch.forEach(id => fetchEntryDetails(id));
      }
    } catch (err) {
      console.error('[GeneralJournal] Error fetching details in batch:', err);
      // Fallback to individual requests on error
      idsToFetch.forEach(id => fetchEntryDetails(id));
    } finally {
      // Remove from loading set
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        idsToFetch.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [entryDetailsCache, loadingDetails, flattenEntryDetails]);

  // Fetch entry details when a row is expanded (single header)
  const fetchEntryDetails = useCallback(async (entryHeaderId) => {
    // Eğer zaten cache'de varsa, tekrar yükleme
    if (entryDetailsCache.has(entryHeaderId)) {
      return;
    }

    // Eğer zaten yükleniyorsa, tekrar başlatma
    if (loadingDetails.has(entryHeaderId)) {
      return;
    }

    try {
      setLoadingDetails(prev => new Set(prev).add(entryHeaderId));
      
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entry-details?entryHeaderId=${entryHeaderId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          console.error('[GeneralJournal] Backend error:', data.error);
          return;
        }
        
        // EntryDetails'leri düzleştirilmiş formata dönüştür
        const flattenedDetails = flattenEntryDetails(data.entryHeader);
        
        // Cache'e ekle
        setEntryDetailsCache(prev => new Map(prev).set(entryHeaderId, flattenedDetails));
      } else {
        const errorText = await response.text();
        console.error('[GeneralJournal] Error fetching details:', errorText);
      }
    } catch (err) {
      console.error('Error fetching entry details:', err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(entryHeaderId);
        return newSet;
      });
    }
  }, [entryDetailsCache, loadingDetails, flattenEntryDetails]);

  const handleDocumentChange = (option) => {
    if (option) {
      setSelectedDocument(option.doc);
      setPage(1);
      setExpandedEntries(new Set());
      setEntryDetailsCache(new Map());
      setError(null);
      setShouldExpandAll(false);
    } else {
      setSelectedDocument(null);
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setError(null);
      setShouldExpandAll(false);
      setAccountCodeSearch('');
      setDebouncedAccountCodeSearch('');
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return '';
    const start = formatDate(doc.periodCoveredStart);
    const end = formatDate(doc.periodCoveredEnd);
    return `${start} - ${end} (${doc.uniqueId || doc.id || ''})`;
  };

  const documentOptions = useMemo(
    () => documents.map((doc) => ({
      value: doc.id ?? doc.uniqueId,
      label: getDocumentLabel(doc),
      doc,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documents]
  );

  const selectedDocOption = useMemo(() => {
    if (!selectedDocument) return null;
    return documentOptions.find(
      (o) =>
        (o.doc.id && selectedDocument.id && o.doc.id === selectedDocument.id) ||
        (o.doc.uniqueId === selectedDocument.uniqueId &&
          o.doc.periodCoveredStart === selectedDocument.periodCoveredStart &&
          o.doc.periodCoveredEnd === selectedDocument.periodCoveredEnd)
    ) ?? null;
  }, [selectedDocument, documentOptions]);

  // Excel indir: tüm yevmiye (tüm sayfalar) + her entry'nin tüm detay satırları
  const handleDownloadExcel = useCallback(async () => {
    if (!selectedDocument) {
      toast.error('Please select a period first to download Excel.');
      return;
    }

    const progressToastId = toast.loading('Preparing Excel export...', { closeOnClick: false });
    const updateProgress = (message, opts = {}) => {
      toast.update(progressToastId, {
        render: message,
        isLoading: opts.isLoading ?? true,
        type: opts.type,
        autoClose: opts.autoClose,
        closeOnClick: opts.closeOnClick ?? false,
      });
    };

    const formatNum = (value) => {
      if (value === null || value === undefined) return '0,00';
      const number = parseFloat(value);
      if (isNaN(number)) return '0,00';
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
    };
    const formatDt = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const pageSize = 500;
    let allHeaders = [];

    try {
      updateProgress('Fetching journal entries...');
      const firstRes = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=0&size=${pageSize}`
      );
      if (!firstRes.ok) {
        updateProgress('Failed to fetch journal entries.', { isLoading: false, type: 'error', autoClose: 4000 });
        return;
      }
      const firstData = await firstRes.json();
      if (firstData.error) {
        updateProgress(firstData.error || 'Failed to fetch journal entries.', { isLoading: false, type: 'error', autoClose: 5000 });
        return;
      }
      const firstBatch = firstData.entryHeaders || [];
      allHeaders = [...firstBatch];
      const totalPages = firstData.totalPages || 1;
      const totalElements = firstData.totalElements || firstBatch.length;

      if (totalPages > 1) {
        // Avoid firing too many parallel requests on large datasets
        for (let p = 1; p < totalPages; p++) {
          updateProgress(`Fetching journal entries... (${p + 1}/${totalPages})`);
          const res = await AuthService.authenticatedFetch(
            `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=${p}&size=${pageSize}`
          );
          if (!res.ok) continue;
          const data = await res.json();
          const batch = data?.entryHeaders || [];
          allHeaders = allHeaders.concat(batch);
        }
      }

      if (allHeaders.length === 0) {
        updateProgress('No journal entries found for this period.', { isLoading: false, type: 'warning', autoClose: 3500 });
        return;
      }
      updateProgress(`Found ${totalElements || allHeaders.length} entries. Preparing details...`);
    } catch (err) {
      console.error('Excel: yevmiye listesi alınırken hata', err);
      updateProgress('Failed to fetch journal entries. Please try again.', { isLoading: false, type: 'error', autoClose: 5000 });
      return;
    }

    let detailsMap = new Map(entryDetailsCache);
    const idsToFetch = allHeaders.filter((h) => !detailsMap.has(h.id)).map((h) => h.id);
    if (idsToFetch.length > 0) {
      updateProgress(`Fetching entry details... (0/${idsToFetch.length})`);

      const CHUNK_SIZE = 100; // keep querystring safe, avoid resource exhaustion
      try {
        for (let i = 0; i < idsToFetch.length; i += CHUNK_SIZE) {
          const chunk = idsToFetch.slice(i, i + CHUNK_SIZE);
          const idsString = chunk.join(',');
          const done = Math.min(i + chunk.length, idsToFetch.length);
          updateProgress(`Fetching entry details... (${done}/${idsToFetch.length})`);

          const response = await AuthService.authenticatedFetch(
            `${EDEFTER_API_URL}/journal-entry-details-batch?entryHeaderIds=${idsString}`
          );
          if (!response.ok) {
            throw new Error(`Batch details request failed (status=${response.status})`);
          }

          const data = await response.json();
          const batchResults = Array.isArray(data) ? data : (data?.results || data?.entryHeaders || []);

          batchResults.forEach((entryHeaderData) => {
            const headerId = entryHeaderData?.entryHeaderId ?? entryHeaderData?.entryHeader?.id;
            if (!headerId) return;
            const flattenedDetails = flattenEntryDetails(entryHeaderData);
            detailsMap.set(headerId, flattenedDetails);
          });
        }
      } catch (err) {
        console.error('Excel: detaylar yüklenirken hata', err);
        updateProgress('Failed to fetch entry details. Please try again.', { isLoading: false, type: 'error', autoClose: 5000 });
        return;
      }
    }

    updateProgress('Generating Excel file...');
    const headerRow = [
      'Entry #',
      'Entry Date',
      'Entry Comment',
      'Entry Debit',
      'Entry Credit',
      'Line #',
      'Posting Date',
      'Account Code',
      'Account Name',
      'Sub Account',
      'Debit',
      'Credit',
      'Comment',
    ];
    const rows = [headerRow];

    allHeaders.forEach((header) => {
      const details = detailsMap.get(header.id) || [];
      rows.push([
        header.entryNumber || '-',
        formatDt(header.enteredDate),
        header.entryComment || '',
        formatNum(header.totalDebit),
        formatNum(header.totalCredit),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
      details.forEach((d) => {
        const debitVal = d.debitCreditCode === 'D' ? formatNum(d.amount) : '';
        const creditVal = d.debitCreditCode === 'C' ? formatNum(d.amount) : '';
        const subAccount = d.accountSubId
          ? `${d.accountSubId}${d.accountSubDescription ? ' - ' + d.accountSubDescription : ''}`
          : '';
        rows.push([
          '',
          '',
          '',
          '',
          '',
          d.lineNumber ?? '',
          formatDt(d.postingDate),
          d.accountMainId ?? '',
          d.accountMainDescription ?? '',
          subAccount,
          debitVal,
          creditVal,
          d.detailComment ?? '',
        ]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'General Journal');
    let filename = 'general_journal';
    if (selectedDocument?.periodCoveredStart && selectedDocument?.periodCoveredEnd) {
      filename = `general_journal_${selectedIdentifier || 'export'}_${selectedDocument.periodCoveredStart}_${selectedDocument.periodCoveredEnd}`;
    }

    updateProgress('Downloading Excel...');
    XLSX.writeFile(wb, `${filename}.xlsx`);
    updateProgress(`Done. Exported ${allHeaders.length} journal entries.`, { isLoading: false, type: 'success', autoClose: 3000, closeOnClick: true });
  }, [
    selectedDocument,
    entryDetailsCache,
    selectedIdentifier,
    flattenEntryDetails,
  ]);

  // Check if account code starts with search term (strict matching)
  const matchesAccountCode = useCallback((accountMainId, accountSubId, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    const mainId = (accountMainId || '').toLowerCase();
    const subId = (accountSubId || '').toLowerCase();
    // Use startsWith instead of includes - '12' matches '120', '121' but not '612'
    return mainId.startsWith(term) || (subId && subId.startsWith(term));
  }, []);

  // EntryHeaders zaten sayfalı geldiği için direkt kullanabiliriz
  // Strict filtering: Only show headers with matching details
  const paginatedGroups = useMemo(() => {
    const searchTerm = debouncedAccountCodeSearch;
    const hasSearch = searchTerm && searchTerm.trim();

    const groups = entryHeaders
      .map(header => {
        const allDetails = entryDetailsCache.get(header.id) || [];
        const isExpanded = expandedEntries.has(header.id);
        
        // Filter details based on search term (startsWith logic)
        const filteredDetails = hasSearch
          ? allDetails.filter(detail => 
              matchesAccountCode(detail.accountMainId, detail.accountSubId, searchTerm)
            )
          : allDetails;
        
        // STRICT FILTERING: If search exists, only show headers with matching details
        // If details are not loaded yet, show header temporarily (will be filtered once details load)
        if (hasSearch) {
          if (allDetails.length === 0) {
            // Details not loaded yet - show header temporarily with loading indicator
            // Details will be loaded in background and header will be filtered automatically once loaded
          } else {
            // Details are loaded - strict check: must have at least one matching detail
            if (filteredDetails.length === 0) {
              return null; // Exclude this header - no matching details
            }
          }
        }
        
        // Detayların toplamlarını filtrelemiş detaylardan hesapla
        const detailTotalDebit = filteredDetails.reduce((sum, detail) => {
          return sum + (detail.debitCreditCode === 'D' ? parseFloat(detail.amount || 0) : 0);
        }, 0);
        const detailTotalCredit = filteredDetails.reduce((sum, detail) => {
          return sum + (detail.debitCreditCode === 'C' ? parseFloat(detail.amount || 0) : 0);
        }, 0);
        
        return {
          headerId: header.id,
          entryNumber: header.entryNumber,
          entryComment: header.entryComment,
          enteredBy: header.enteredBy,
          enteredDate: header.enteredDate,
          totalDebit: header.totalDebit,
          totalCredit: header.totalCredit,
          details: filteredDetails, // Filtered details (only matching ones)
          detailTotalDebit: detailTotalDebit,
          detailTotalCredit: detailTotalCredit,
          isExpanded: isExpanded,
          isLoadingDetails: allDetails.length === 0 && hasSearch, // Show loading indicator if details not loaded and search is active
        };
      })
      .filter(group => group !== null); // Remove filtered out headers
    
    return groups;
  }, [entryHeaders, entryDetailsCache, expandedEntries, debouncedAccountCodeSearch, matchesAccountCode]);

  const handleToggleEntry = (headerId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(headerId)) {
      newExpanded.delete(headerId);
    } else {
      newExpanded.add(headerId);
      // Detayları yükle (eğer cache'de yoksa)
      if (!entryDetailsCache.has(headerId)) {
        fetchEntryDetails(headerId);
      }
    }
    setExpandedEntries(newExpanded);
  };

  const handleExpandAll = useCallback(() => {
    if (!selectedDocument) return;
    
    // Tüm entry header'ları için details yükle ve expanded yap
    const allHeaderIds = new Set(entryHeaders.map(h => h.id));
    allHeaderIds.forEach(headerId => {
      if (!entryDetailsCache.has(headerId)) {
        fetchEntryDetails(headerId);
      }
    });
    setExpandedEntries(allHeaderIds);
    setShouldExpandAll(true);
  }, [selectedDocument, entryHeaders, entryDetailsCache, fetchEntryDetails]);

  const handleCollapseAll = useCallback(() => {
    setExpandedEntries(new Set());
    setShouldExpandAll(false);
  }, []);

  const allExpanded = entryHeaders.length > 0 && expandedEntries.size === entryHeaders.length && shouldExpandAll;

  const handleExpandCollapseToggle = (event) => {
    if (event.target.checked) {
      handleExpandAll();
    } else {
      handleCollapseAll();
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white hidden md:block">
          {t('edefter.generalJournalTitle')}
        </h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
          >
            <Upload size={16} />
            {t('edefter.uploadJournal')}
          </button>
          {selectedDocument && entryHeaders.length > 0 && (
            <button
              type="button"
              onClick={handleDownloadExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95 whitespace-nowrap"
            >
              <Download size={16} />
              {t('edefter.downloadExcel')}
            </button>
          )}
        </div>
      </div>

      <div className={`${cardClass} overflow-hidden`}>
        <div className="p-6 md:p-8 space-y-8">
          <div>
            <SectionHeader icon={SlidersHorizontal} label={t('edefter.filters')} />
            {error && (
              <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl px-4 py-3 mb-4">
                <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600 dark:text-red-400 font-medium flex-1">{error}</p>
                <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X size={14} />
                </button>
              </div>
            )}
            {!selectedIdentifier && (
              <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 mb-4">
                <AlertTriangle size={15} className="text-blue-500 flex-shrink-0" />
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{t('edefter.noCompanySelected')}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-5 space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <Calendar size={13} className="text-[#24299d] dark:text-[#818cf8]" />
                  {t('edefter.journalPeriod')}
                </label>
                <Select
                  options={documentOptions}
                  value={selectedDocOption}
                  onChange={handleDocumentChange}
                  placeholder={t('edefter.selectPeriod')}
                  isLoading={loadingDocuments}
                  isClearable
                  isDisabled={!selectedIdentifier}
                  styles={getSelectStyles(darkMode)}
                  noOptionsMessage={() => t('edefter.noDataFound')}
                  loadingMessage={() => t('edefter.loadingDocuments')}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="md:col-span-4 space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <Search size={13} className="text-[#24299d] dark:text-[#818cf8]" />
                  {t('edefter.accountCodeSearch')}
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={accountCodeSearch}
                    onChange={(e) => setAccountCodeSearch(e.target.value)}
                    disabled={!selectedDocument}
                    placeholder={t('edefter.accountCodePlaceholder')}
                    className={`${inputClass} pl-9 pr-9 disabled:opacity-50`}
                  />
                  {loading && debouncedAccountCodeSearch ? (
                    <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#24299d] animate-spin" />
                  ) : accountCodeSearch ? (
                    <button
                      type="button"
                      onClick={() => setAccountCodeSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <X size={16} />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="md:col-span-3 flex flex-col items-center justify-end pb-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={allExpanded}
                  onClick={() => handleExpandCollapseToggle({ target: { checked: !allExpanded } })}
                  disabled={!selectedDocument || entryHeaders.length === 0}
                  className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    allExpanded ? 'bg-[#24299d]' : 'bg-gray-300 dark:bg-white/20'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      allExpanded ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-[11px] font-medium mt-1.5 ${allExpanded ? 'text-[#24299d] dark:text-[#818cf8]' : 'text-gray-500'}`}>
                  {allExpanded ? t('edefter.collapseAll') : t('edefter.expandAll')}
                </span>
              </div>
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="p-4 bg-[#24299d]/10 dark:bg-[#24299d]/20 rounded-2xl">
                <Loader2 size={32} className="text-[#24299d] dark:text-[#818cf8] animate-spin" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('edefter.loadingEntries')}</p>
            </div>
          )}

          {!loading && entryHeaders.length > 0 && (
            <div>
              <SectionHeader
                icon={BookOpen}
                label={t('edefter.entriesTitle')}
                right={
                  <span className="text-xs text-gray-400 font-medium">
                    {t('edefter.entriesCount', { total: totalElements, page, totalPages })}
                  </span>
                }
              />
              <div className="rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs md:text-sm min-w-[800px]">
                <thead className="bg-gray-50 dark:bg-white/5 text-gray-500 text-left">
                  <tr>
                    <th className="px-3 md:px-6 py-3 md:py-4 w-10" />
                    <th className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">{t('edefter.entryNumber')}</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">{t('edefter.date')}</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">{t('edefter.comment')}</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs md:text-sm font-semibold">{t('edefter.debit')}</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs md:text-sm font-semibold">{t('edefter.credit')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                  {paginatedGroups.map((group) => {
                    const isExpanded = expandedEntries.has(group.headerId);
                    const isLoadingRowDetails = loadingDetails.has(group.headerId);
                    const hasDetails = group.details.length > 0;
                    const shouldShowDetails = isExpanded && (hasDetails || isLoadingRowDetails);
                    const totalsEqual = Math.abs(group.detailTotalDebit - group.detailTotalCredit) < 0.01;

                    return (
                      <React.Fragment key={`entry-${group.headerId}`}>
                        <tr
                          className="hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                          onClick={() => handleToggleEntry(group.headerId)}
                        >
                          <td className="px-3 md:px-6 py-3 md:py-4">
                            {isExpanded
                              ? <ChevronDown size={16} className="text-[#24299d] dark:text-[#818cf8]" />
                              : <ChevronRight size={16} className="text-gray-400" />}
                          </td>
                          <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">
                            <span className="flex items-center gap-2">
                              {group.entryNumber || '-'}
                              {group.isLoadingDetails && <Loader2 size={12} className="animate-spin text-gray-400" />}
                            </span>
                          </td>
                          <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm">{formatDate(group.enteredDate)}</td>
                          <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">{group.entryComment || '-'}</td>
                          <td className="px-3 md:px-6 py-3 md:py-4 text-right text-xs md:text-sm font-bold">{formatNumber(group.totalDebit)}</td>
                          <td className="px-3 md:px-6 py-3 md:py-4 text-right text-xs md:text-sm font-bold">{formatNumber(group.totalCredit)}</td>
                        </tr>

                        {shouldShowDetails && (
                          <tr>
                            <td colSpan={6} className="p-0 border-none">
                              <div className="bg-gray-50/60 dark:bg-white/[0.02] border-t border-gray-100 dark:border-white/5 px-3 md:px-6 py-3">
                                <table className="w-full text-xs min-w-[700px]">
                                  <thead>
                                    <tr className="text-gray-400 text-left">
                                      <th className="py-2 pr-3 font-semibold w-8" />
                                      <th className="py-2 pr-3 font-semibold">{t('edefter.lineNumber')}</th>
                                      <th className="py-2 pr-3 font-semibold">{t('edefter.postingDate')}</th>
                                      <th className="py-2 pr-3 font-semibold">{t('edefter.accountCode')}</th>
                                      <th className="py-2 pr-3 font-semibold">{t('edefter.accountName')}</th>
                                      <th className="py-2 pr-3 font-semibold">{t('edefter.subAccount')}</th>
                                      <th className="py-2 pr-3 font-semibold text-right">{t('edefter.debit')}</th>
                                      <th className="py-2 pr-3 font-semibold text-right">{t('edefter.credit')}</th>
                                      <th className="py-2 font-semibold">{t('edefter.comment')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100/80 dark:divide-white/5">
                                    {isLoadingRowDetails ? (
                                      <tr>
                                        <td colSpan={9} className="py-4 text-center text-gray-500">
                                          <span className="inline-flex items-center gap-2">
                                            <Loader2 size={14} className="animate-spin" />
                                            {t('edefter.loadingDetails')}
                                          </span>
                                        </td>
                                      </tr>
                                    ) : hasDetails ? (
                                      <>
                                        {group.details.map((detail, idx) => (
                                          <tr key={`detail-${detail.entryDetailId}-${idx}`} className="hover:bg-white/60 dark:hover:bg-white/5">
                                            <td className="py-2 pr-3" />
                                            <td className="py-2 pr-3">{detail.lineNumber || '-'}</td>
                                            <td className="py-2 pr-3 text-gray-500">{formatDate(detail.postingDate)}</td>
                                            <td className="py-2 pr-3">{detail.accountMainId || '-'}</td>
                                            <td className="py-2 pr-3">{detail.accountMainDescription || '-'}</td>
                                            <td className="py-2 pr-3 text-gray-500">
                                              {detail.accountSubId ? `${detail.accountSubId} - ${detail.accountSubDescription || ''}` : '-'}
                                            </td>
                                            <td className="py-2 pr-3 text-right font-semibold">
                                              {detail.debitCreditCode === 'D' ? formatNumber(detail.amount) : '-'}
                                            </td>
                                            <td className="py-2 pr-3 text-right font-semibold">
                                              {detail.debitCreditCode === 'C' ? formatNumber(detail.amount) : '-'}
                                            </td>
                                            <td className="py-2 text-gray-500">{detail.detailComment || '-'}</td>
                                          </tr>
                                        ))}
                                        <tr className="border-t-2 border-[#24299d]/20">
                                          <td className="py-2 pr-3" />
                                          <td colSpan={5} className="py-2 pr-3 text-right font-bold text-xs md:text-sm">
                                            {t('edefter.total')}:
                                          </td>
                                          <td className={`py-2 pr-3 text-right font-bold ${totalsEqual ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                                            {formatNumber(group.detailTotalDebit)}
                                          </td>
                                          <td className={`py-2 pr-3 text-right font-bold ${totalsEqual ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                                            {formatNumber(group.detailTotalCredit)}
                                          </td>
                                          <td />
                                        </tr>
                                      </>
                                    ) : (
                                      <tr>
                                        <td colSpan={9} className="py-4 text-center text-gray-500">
                                          {debouncedAccountCodeSearch?.trim()
                                            ? t('edefter.noDetailsMatch', { code: debouncedAccountCodeSearch })
                                            : t('edefter.noDetailsFound')}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                    className="h-9 w-9 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => {
                      setPage((p) => p - 1);
                      if (!shouldExpandAll) setExpandedEntries(new Set());
                    }}
                    className="h-9 w-9 rounded-xl border border-gray-200 dark:border-white/10 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-3 text-sm font-medium text-gray-600 dark:text-gray-300">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => {
                      setPage((p) => p + 1);
                      if (!shouldExpandAll) setExpandedEntries(new Set());
                    }}
                    className="h-9 w-9 rounded-xl border border-gray-200 dark:border-white/10 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => {
                      setPage(totalPages);
                      if (!shouldExpandAll) setExpandedEntries(new Set());
                    }}
                    className="h-9 w-9 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    »
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && !selectedDocument && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 rounded-2xl border border-dashed border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02]">
              <div className="p-4 bg-[#24299d]/10 dark:bg-[#24299d]/20 rounded-2xl">
                <BookOpen size={32} className="text-[#24299d] dark:text-[#818cf8]" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{t('edefter.noJournalSelected')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md px-4">{t('edefter.noJournalSelectedHint')}</p>
            </div>
          )}

          {!loading && selectedDocument && entryHeaders.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02]">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('edefter.noEntriesFound')}</p>
            </div>
          )}
        </div>
      </div>

      <EdefterUpload
        asModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default GeneralJournal;

